import { SEO_BY_LOCALE } from './seoConfig.js';
import {
  SUPPORTED_LOCALES,
  localesForPage,
  DEFAULT_LOCALE,
  absoluteUrlForPage,
  pathForPage,
} from '../routes.js';

/**
 * Build hreflang alternate entries (mutual pointers across all locales).
 *
 * @param {string} page
 * @param {string} siteUrl
 * @returns {Array<{ hreflang: string, href: string }>}
 */
export function buildHreflangAlternates(page, siteUrl) {
  const alternates = localesForPage(page).map((locale) => ({
    hreflang: locale,
    href: absoluteUrlForPage(siteUrl, page, locale),
  }));

  alternates.push({
    hreflang: 'x-default',
    href: absoluteUrlForPage(siteUrl, page, DEFAULT_LOCALE),
  });

  return alternates;
}

/**
 * Build hreflang link tags for a page.
 *
 * @param {string} page
 * @param {string} siteUrl
 * @returns {string[]}
 */
function buildHreflangTags(page, siteUrl) {
  return buildHreflangAlternates(page, siteUrl).map(
    ({ hreflang, href }) => `<link rel="alternate" hreflang="${hreflang}" href="${escapeAttr(href)}" />`
  );
}

/**
 * Build SEO metadata for a page.
 *
 * @param {object} options
 * @param {string} options.page - Page id: home | why | hardware | privacy | terms
 * @param {string} options.locale - i18n language code
 * @param {string} options.siteUrl - Site origin without trailing slash
 * @param {import('i18next').TFunction} options.t - i18n translate function
 * @returns {object}
 */
export function getPageSeo({ page, locale, siteUrl, t }) {
  page = page === 'homepagePreview' ? 'home' : page;
  const homeSeo = locale.startsWith('zh') ? {
    title: 'AquaMind | 零售机器人与品牌 IP 伙伴的实体 AI 平台',
    description: 'AquaMind 将品牌知识、AI 对话与设备交互连接起来，为零售机器人、智能公仔与品牌 IP 伙伴提供统一的实体 AI 平台。',
  } : {
    title: 'AquaMind | Physical AI for Retail Robots & Brand Companions',
    description: 'AquaMind connects brand knowledge, AI dialogue and device interaction in one physical AI platform for retail robots and brand & IP companions.',
  };
  const seo = SEO_BY_LOCALE[locale] || SEO_BY_LOCALE.en;
  const canonicalUrl = absoluteUrlForPage(siteUrl, page, locale);
  const imageUrl = `${siteUrl.replace(/\/$/, '')}/Aquamind_blue_logo.png`;

  const title = page === 'privacy'
    ? `AquaMind | ${t('legal.privacy.title')}`
    : page === 'terms'
      ? `AquaMind | ${t('legal.terms.title')}`
      : page === 'hardware'
        ? `AquaMind | ${t('hardware.seoTitle')}`
        : page === 'homepagePrevious'
          ? `AquaMind | ${locale.startsWith('zh') ? '已保存的旧版首页' : 'Previous homepage (archived)'}`
          : page === 'home' ? homeSeo.title : seo.title;

  const description = page === 'privacy'
    ? t('legal.privacy.intro')
    : page === 'terms'
      ? t('legal.terms.intro')
      : page === 'hardware'
        ? t('hardware.seoDescription')
        : page === 'home' ? homeSeo.description : seo.description;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl.replace(/\/$/, '')}/#organization`,
        name: 'AquaMind',
        url: canonicalUrl,
        logo: imageUrl,
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl.replace(/\/$/, '')}/#website`,
        url: canonicalUrl,
        name: 'AquaMind',
        description,
        publisher: { '@id': `${siteUrl.replace(/\/$/, '')}/#organization` },
        inLanguage: locale,
      },
      {
        '@type': 'Service',
        '@id': `${siteUrl.replace(/\/$/, '')}/#service`,
        name: 'AquaMind Physical AI Platform',
        serviceType: 'Physical AI platform for retail robots and brand companions',
        provider: { '@id': `${siteUrl.replace(/\/$/, '')}/#organization` },
        url: canonicalUrl,
        description,
        areaServed: 'Worldwide',
        availableLanguage: [...localesForPage(page)],
      },
    ],
  };

  return {
    title,
    robots: page === 'homepagePrevious' ? 'noindex,follow' : 'index,follow',
    description,
    canonicalUrl,
    imageUrl,
    locale,
    ogLocale: seo.locale,
    htmlLang: locale,
    structuredData,
    hreflangTags: buildHreflangTags(page, siteUrl),
    hreflangAlternates: buildHreflangAlternates(page, siteUrl),
    routePath: pathForPage(page, locale),
  };
}

/**
 * Render HTML tags for document head injection during SSG.
 *
 * @param {ReturnType<typeof getPageSeo>} seo
 * @returns {string}
 */
export function renderSeoHead(seo) {
  const jsonLd = JSON.stringify(seo.structuredData).replace(/</g, '\\u003c');

  return [
    `<title>${escapeHtml(seo.title)}</title>`,
    `<meta name="description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="robots" content="${seo.robots}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="AquaMind" />`,
    `<meta property="og:title" content="${escapeAttr(seo.title)}" />`,
    `<meta property="og:description" content="${escapeAttr(seo.description)}" />`,
    `<meta property="og:url" content="${escapeAttr(seo.canonicalUrl)}" />`,
    `<meta property="og:image" content="${escapeAttr(seo.imageUrl)}" />`,
    `<meta property="og:locale" content="${escapeAttr(seo.ogLocale)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttr(seo.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(seo.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttr(seo.imageUrl)}" />`,
    `<link rel="canonical" href="${escapeAttr(seo.canonicalUrl)}" />`,
    ...seo.hreflangTags,
    `<script type="application/ld+json" data-seo="structured-data">${jsonLd}</script>`,
  ].join('\n    ');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}
